// src/pages/Customer/Payment/PaytrPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useCart } from "../../../context/CartContext/CartContext";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { paytrApi } from "../../../api/paytr";
import { useTheme } from "../../../context/ThemeContext";

const PaytrPage: React.FC = () => {
  const { theme } = useTheme();
  const S = useMemo(() => {
    const bg = theme?.backgroundColor || "#0f1115";
    const textPrimary = theme?.textPrimary || "#fff";
    const textSecondary = theme?.textSecondary || "rgba(255,255,255,.7)";
    return { bg, textPrimary, textSecondary };
  }, [theme]);

  const nav = useNavigate();
  const [sp] = useSearchParams();
  const okUrl = sp.get("ok") || `${window.location.origin}/musteri/odeme/basarili`;
  const failUrl = sp.get("fail") || `${window.location.origin}/musteri/odeme/hata`;

  const { header, refresh, visitorKey } = useCart();
  const { customer } = useCustomerAuth();

  const [token, setToken] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // --- iFrame yüksekliği (mobilde ekrana tam sığdır) ---
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeH, setIframeH] = useState<number>(740);

  useEffect(() => {
    const computeHeight = () => {
      // safe-area insets (iOS çentik vb.)
      const insetTop = Number(getComputedStyle(document.documentElement).getPropertyValue("--sat").replace("px", "")) || 0;
      const insetBottom = Number(getComputedStyle(document.documentElement).getPropertyValue("--sab").replace("px", "")) || 0;

      // 100dvh kullanımı için fallback: innerHeight
      const vh = window.innerHeight;

      // Sayfa iç kenar boşluklarını (p-*) çok büyük yapmadık, ama yine de düşelim:
      const verticalPadding = 16 /* top */ + 16 /* bottom */ + insetTop + insetBottom;

      // Mobilde: tam ekran - padding; min 520px
      // Tablet/desktop: en az 700-740px
      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      const h = isDesktop ? Math.max(740, vh - 120) : Math.max(520, vh - verticalPadding);

      setIframeH(Math.round(h));
    };

    // safe-area custom CSS vars (Safari'de env() fallback'ı)
    const root = document.documentElement;
    root.style.setProperty("--sat", `env(safe-area-inset-top, 0px)`);
    root.style.setProperty("--sab", `env(safe-area-inset-bottom, 0px)`);

    computeHeight();
    window.addEventListener("resize", computeHeight);
    window.addEventListener("orientationchange", computeHeight);
    return () => {
      window.removeEventListener("resize", computeHeight);
      window.removeEventListener("orientationchange", computeHeight);
    };
  }, []);

  // iFrameResizer yükle
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://www.paytr.com/js/iframeResizer.min.js";
    s.onload = () => (window as any).iFrameResize?.({ checkOrigin: false }, "#paytriframe");
    document.body.appendChild(s);
    return () => {
      document.body.removeChild(s);
    };
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
  
      try {
        await refresh();
  
        const email = customer?.email || "guest@example.com";
  
        let checkoutContext: any = null;
        try {
          const raw = localStorage.getItem("checkout_context");
          checkoutContext = raw ? JSON.parse(raw) : null;
        } catch {
          checkoutContext = null;
        }
  
        const res = await paytrApi.createTokenFromCart({
          sepetId: header?.sepetId ?? null,
          musteriId: customer?.id ?? null,
          ziyaretciAnahtari: customer?.id ? null : visitorKey,
  
          email,
          userIp: "",
          userName: customer?.name || null,
          userAddress: null,
          userPhone: customer?.telefon || null,
  
          testMode: false,
          noInstallment: false,
          maxInstallment: 0,
          currency: checkoutContext?.currency ?? "TL",
          debugOn: true,
  
          // ✅ checkout sayfasında localStorage'a attığın alanlar backend'e gidiyor
          faturaAdresiJson: checkoutContext?.faturaAdresiJson ?? null,
          teslimatAdresiJson: checkoutContext?.teslimatAdresiJson ?? null,
          notlar: checkoutContext?.note ?? null,
  
          okUrl,
          failUrl,
        });
  
        if (!res.success || !res.data) {
          throw new Error(res.errorDetail || "PAYTR token alınamadı");
        }
  
        setToken(res.data.token);
      } catch (e: any) {
        setErr(e?.message || "PAYTR iFrame açılamadı.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, visitorKey, header?.sepetId, okUrl, failUrl]);
  if (loading) {
    return (
      <div
        className="min-h-screen grid place-items-center px-4"
        style={{ background: S.bg, color: S.textSecondary }}
      >
        <div className="text-center">
          <Loader2 className="animate-spin inline-block mr-2" />
          Ödeme sayfası hazırlanıyor…
        </div>
      </div>
    );
  }

  if (err || !token) {
    return (
      <div className="max-w-xl mx-auto p-4 sm:p-6" style={{ background: S.bg }}>
        <div
          className="rounded-lg p-4 border"
          style={{ borderColor: "rgba(239,68,68,.4)", background: "rgba(239,68,68,.08)" }}
        >
          <div className="font-semibold mb-1" style={{ color: "#b91c1c" }}>
            Ödeme başlatılamadı
          </div>
          <div className="text-sm mb-3" style={{ color: S.textSecondary }}>
            {err || "Bilinmeyen hata"}
          </div>
          <button
            onClick={() => nav("/musteri/checkout")}
            className="h-10 px-3 rounded-md text-white"
            style={{ background: "#EF4444" }}
          >
            Geri dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full mx-auto px-4 sm:px-5 md:px-6"
      style={{
        background: S.bg,
        minHeight: "100svh", // küçük viewport unit: mobilde toolbar sorunlarını çözer
        paddingTop: "max(16px, env(safe-area-inset-top))",
        paddingBottom: "max(16px, env(safe-area-inset-bottom))",
      }}
    >
      <div className="w-full max-w-4xl mx-auto">
        <iframe
          id="paytriframe"
          src={`https://www.paytr.com/odeme/guvenli/${token}`}
          frameBorder={0}
          scrolling="auto"        // ⬅️ kaydırmayı aç
          style={{
            width: "100%",
            height: "calc(100svh - 48px)", // üst/alt padding düşülmüş tam ekran
            minHeight: 560,                // desktopta da yeterli alan
            borderRadius: 12,
            background: "#fff",
            display: "block",
          }}
          title="PAYTR Ödeme"
        />
        <div className="text-xs mt-2" style={{ color: S.textSecondary }}>
          Ödeme ekranı güvenli PAYTR altyapısı üzerinden açılmıştır.
        </div>
      </div>
    </div>
  );

};

export default PaytrPage;
